import { useEffect, useState, useContext, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import { formatTransactionDate } from "../../utils/sastDate";
import { isValidSACellphone, validateSACellphone } from "../../utils/systemValidation";
import { normalizePolicyAddonsForForm } from "../../utils/addonHelpers";
import {
  getApplicationSendBlockReason,
  getGeneratedDocumentsMissingLink,
  validateSelectedDocumentsForSend,
} from "../../utils/documentCompliance";
import { invalidateCompliance } from "../../utils/complianceInvalidate";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Layout,
  theme,
  Tabs,
  Row,
  Col,
  Card,
  Button,
  Input,
  message,
  Table,
  Modal,
  Tag,
  Alert,
  Tooltip,
  Dropdown,
  Menu,
} from "antd";
import {
  UserOutlined,
  TeamOutlined,
  ArrowLeftOutlined,
  SolutionOutlined,
  MailOutlined,
  WalletOutlined,
  CreditCardOutlined,
  DollarOutlined,
  FileTextOutlined,
  HistoryOutlined,
  EditOutlined,
  DownOutlined,
  FilePdfOutlined,
  SendOutlined,
  ExceptionOutlined,
  StopOutlined,
  FileDoneOutlined,
} from "@ant-design/icons";
import type { TabsProps } from "antd";
import LoadingOverlay from "react-loading-overlay-ts";

import TopBar from "../../components/TopBar";
import SideBar from "../../components/SideBar";
import Documentlist from "../../components/DocumentList";
import EditMemberModal from "../../components/ModalForm/Member/EditMemberModal";
import GenerateDocumentModal from "../../components/ModalForm/Member/GenerateDocumentModal";
import SendDocumentModal from "../../components/ModalForm/Member/SendDocumentModal";
import { LapsePolicyModal, CancelPolicyModal, ActivatePolicyModal } from "../../components/ModalForm";
import LoadPremiumModal from "../../components/ModalForm/Member/LoadPremiumModal";
import EditPaymentMethodModal from "../../components/ModalForm/Member/EditPaymentMethodModal";

import {
  getMemberApi,
  editMemberApi,
  sendMemberApplicationApi,
  sendMemberSmsApi,
  lapseMemberPolicyApi,
  activateMemberPolicyApi,
  loadMemberPremiumApi,
} from "../../services/Member/MemberApis";
import { addTransactionApi, removeTransactionApi, getMemberTransactionsApi } from "../../services/Transaction/TransactionApis";
import { removePaymentApi, addPaymentApi, editPaymentApi } from "../../services/Payment/PaymentApis";
import AddPaymentMethodModal from "../../components/ModalForm/Member/AddPaymentMethodModal";
import {
  addMemberDocumentApi,
  downloadDocumentApi,
  removeDocumentApi,
  removeMemberDocumentApi,
  generateDocumentApi,
  sendDocumentApi,
} from "../../services/Document/DocumentApis";
import { getCompanyClaimsApi } from "../../services/Claim/ClaimApis";
import PaymentTable from "../../components/Table/PaymentTable";
import AuditTable from "../../components/Table/AuditTable";
import { AuthContext } from "../../context/AuthContext";
import { useFormContext } from "../../hooks/useFormContext";
import { TourContext } from "../../context/TourContext";
import { toast } from "../../utils/toast";

const ACTIVATE_CELLPHONE_TOOLTIP =
  "Invalid cellphone — edit the member and correct the number before activating";

const { Content } = Layout;

const TAB_KEYS: Record<string, string> = {
  info: "1",
  "payment-info": "2",
  payments: "3",
  documents: "4",
  statement: "5",
  audit: "6",
  claims: "7",
};

const TAB_PARAM_KEYS: Record<string, string> = {
  "1": "info",
  "2": "payment-info",
  "3": "payments",
  "4": "documents",
  "5": "statement",
  "6": "audit",
  "7": "claims",
};

interface PaymentType {
  id?: string;
  Type: string;
  AccountName: string;
  AccountNumber: string;
  AccountType: string;
  BranchCode: string;
  PaymentDate: string;
  PayAtAccountNumber: string;
  StartDate?: string | null;
  Amount?: string | null;
  FirstCollectionDate?: string | null;
  FirstCollectionAmount?: string | null;
  MandateId?: string | number | null;
  MandateInstructionId?: string | number | null;
}

const MemberManagementPage: React.FC = () => {
  const navigate = useNavigate();

  const { currentUser, currentGroup } = useContext(AuthContext);
  const { setProductInfo } = useFormContext();
  const { memberId }: any = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [policyId, setPolicyId] = useState(0);
  const [member, setMember] = useState<any>({});
  const [coveredLive, setCoveredLive] = useState<any>([]);
  const [payment, setPayment] = useState<PaymentType>({
    Type: "N/A",
    AccountName: "N/A",
    AccountNumber: "N/A",
    AccountType: "N/A",
    BranchCode: "N/A",
    PaymentDate: "N/A",
    PayAtAccountNumber: "N/A",
  });
  const [secondaryPayment, setSecondaryPayment] = useState<any>(null);
  const [payments, setPayments] = useState<any>([]);
  const [documents, setDocuments] = useState<any>([]);
  const [audits, setAudits] = useState<any>([]);
  const [ledgerData, setLedgerData] = useState<any[]>([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerLoaded, setLedgerLoaded] = useState(false);
  const [claimsData, setClaimsData] = useState<any[]>([]);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [isActive, setActive] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [smsContent, setSmsContent] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [formattedMember, setFormattedMember] = useState<any>({});
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showAddSecondaryPaymentModal, setShowAddSecondaryPaymentModal] =
    useState(false);
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showSendDocsModal, setShowSendDocsModal] = useState(false);
  const [showLapseModal, setShowLapseModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showCancelPolicyModal, setShowCancelPolicyModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [activeTab, setActiveTab] = useState(
    () => TAB_KEYS[searchParams.get("tab") ?? ""] ?? "1"
  );

  const {
    refEditMember,
    refPaymentMethod,
    refGenerateDocument,
    refSendDocument,
    refLapsePolicy,
    refLoadPremium,
    refActivatePolicy,
  } = useContext(TourContext);

  const applicationSendBlockReason = useMemo(
    () => getApplicationSendBlockReason(documents, member?.Group),
    [documents, member?.Group]
  );

  const documentsMissingLink = useMemo(
    () => getGeneratedDocumentsMissingLink(documents),
    [documents]
  );

  const onChange = (key: string) => {
    setActiveTab(key);
    const tabParam = TAB_PARAM_KEYS[key];
    if (tabParam) {
      setSearchParams({ tab: tabParam }, { replace: true });
    }
  };

  const loadLedger = useCallback(() => {
    if (!memberId) return;
    setLedgerLoading(true);
    getMemberTransactionsApi(String(memberId))
      .then((res) => {
        const sorted = (res.data || []).sort(
          (a: any, b: any) =>
            new Date(b.Date).getTime() - new Date(a.Date).getTime()
        );
        setLedgerData(sorted);
        setLedgerLoaded(true);
        setLedgerLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLedgerLoading(false);
      });
  }, [memberId]);

  const loadClaims = useCallback(() => {
    if (!currentGroup || !memberId || !policyId) return;
    setClaimsLoading(true);
    getCompanyClaimsApi(String(currentGroup))
      .then((res) => {
        const all = res.data || [];
        const mine = all.filter(
          (c: any) =>
            c?.Policy?.id === policyId ||
            c?.PolicyID === policyId ||
            String(c?.Policy?.MemberID) === String(memberId) ||
            String(c?.MemberID) === String(memberId)
        );
        const sorted = mine.sort(
          (a: any, b: any) =>
            new Date(b.Date).getTime() - new Date(a.Date).getTime()
        );
        setClaimsData(sorted);
        setClaimsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setClaimsLoading(false);
      });
  }, [currentGroup, memberId, policyId]);

  // Load lazy tabs on mount / return (e.g. back from Manage Claim with ?tab=claims)
  useEffect(() => {
    if (activeTab === "5" && !ledgerLoaded) {
      loadLedger();
    }
  }, [activeTab, ledgerLoaded, loadLedger]);

  useEffect(() => {
    if (activeTab === "7" && policyId) {
      loadClaims();
    }
  }, [activeTab, policyId, loadClaims]);

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleOpen = () => {
    setIsModalOpen(true);
    setSmsContent("");
  };

  const handleSendSMS = async () => {
    setActive(true);

    return await sendMemberSmsApi(
      memberId,
      { Message: smsContent },
      currentUser.id
    )
      .then((res) => {
        setActive(false);
        setIsModalOpen(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        setIsModalOpen(false);
        console.log(err);
      });
  };

  const handleAddTransaction = async (data: any, memberId: any) => {
    setActive(true);

    return await addTransactionApi(memberId, { ...data, userId: currentUser.id })
      .then((res) => {
        res.data.success && setPayments([...payments, res.data]);
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

    const handleDeleteTransaction = async (id: any, policyId: any) => {
    setActive(true);

    return await removeTransactionApi(id, policyId)
      .then((res) => {
        res.data.success && setPayments([...payments, res.data]);
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleAddDocument = async (id: any, data: any) => {
    setActive(true);

    return await addMemberDocumentApi(id, data)
      .then(async (res) => {
        const doc = res?.data?.data ?? res?.data ?? res;
        setDocuments((prev: any[]) => [
          ...(Array.isArray(prev) ? prev : []),
          {
            ...doc,
            DocumentType: doc?.DocumentType ?? data?.documentType,
            documentType: doc?.documentType ?? data?.documentType,
            Name: doc?.Name ?? data?.name,
          },
        ]);
        try {
          await refreshMember();
        } catch (err) {
          console.log(err);
        }
        invalidateCompliance();
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleDownloadDocument = async (data: any) => {
    if (!data?.Link) {
      message.error("This document has no download link.");
      return;
    }

    setActive(true);

    await downloadDocumentApi({ Link: data.Link })
      .then(async (res) => {
        const presignedUrl = res.data;
        if (!presignedUrl || typeof presignedUrl !== "string") {
          throw new Error("No download URL returned from server.");
        }
        const filename = (data.Link || "").split("/").pop() || `document_${Date.now()}`;

        const fileRes = await fetch(presignedUrl);
        if (!fileRes.ok) {
          throw new Error(`Document fetch failed: ${fileRes.status} ${fileRes.statusText}`);
        }
        const blob = await fileRes.blob();
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);

        message.success("Document has started downloading.");
        setActive(false);
      })
      .catch((err) => {
        setActive(false);
        console.error(err);
        message.error("Error occurred trying to download the document.");
      });
  };

  const handleDeleteDocument = (id: string) => {
    setActive(true);
    removeMemberDocumentApi(id)
      .then(async (res) => {
        const deleted = res.data;
        setDocuments((prev: any[]) =>
          (Array.isArray(prev) ? prev : []).filter(
            (document: any) => document.id !== deleted.id
          )
        );
        try {
          await refreshMember();
        } catch (err) {
          console.log(err);
        }
        invalidateCompliance();
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        message.error(err);
        console.log(err);
      });
  };

  const defaultPayment: PaymentType = {
    Type: "N/A",
    AccountName: "N/A",
    AccountNumber: "N/A",
    AccountType: "N/A",
    BranchCode: "N/A",
    PaymentDate: "N/A",
    PayAtAccountNumber: "N/A",
  };

  const totalLedgerDebit = ledgerData.reduce((sum, row: any) => {
    const amount = Number(row.Amount);
    const isDebit = amount < 0 || row.Type === "Refund";
    return sum + (isDebit ? Math.abs(amount) : 0);
  }, 0);

  const totalLedgerCredit = ledgerData.reduce((sum, row: any) => {
    const amount = Number(row.Amount);
    const isCredit = amount > 0 && row.Type !== "Refund";
    return sum + (isCredit ? amount : 0);
  }, 0);

  const applyMemberPayments = (memberData: any) => {
    if (memberData?.PaymentType) {
      setPayment(memberData.PaymentType);
    } else {
      setPayment(defaultPayment);
    }
    setSecondaryPayment(memberData?.SecondaryPaymentType || null);
  };

  const renderPaymentDetails = (p: any) => {
    if (!p) return null;

    return (
      <>
        <div className="card-content">
          <h4>Payment Type:</h4>
          <span> {p.Type || "N/A"}</span>
        </div>
        <div className="card-content">
          <h4>Account Name:</h4>
          <span>{p.AccountName || "N/A"}</span>
        </div>
        {p.Type === "PayAt" ? (
          <div className="card-content">
            <h4>Pay@ ID:</h4>
            <span style={{ wordBreak: "break-all" }}>
              {p.PayAtAccountNumber || "N/A"}
            </span>
          </div>
        ) : p.Type === "Cash" ? (
          <div className="card-content">
            <h4>Details:</h4>
            <span>Cash payer — no bank mandate</span>
          </div>
        ) : (
          <>
            <div className="card-content">
              <h4>Account Number:</h4>
              <span>{p.AccountNumber || "N/A"}</span>
            </div>
            <div className="card-content">
              <h4>Account Type:</h4>
              <span>{p.AccountType || "N/A"}</span>
            </div>
            <div className="card-content">
              <h4>Branch Code:</h4>
              <span> {p.BranchCode || "N/A"}</span>
            </div>
            <div className="card-content">
              <h4>Payment Date:</h4>
              <span>{p.PaymentDate || "N/A"}</span>
            </div>
          </>
        )}
      </>
    );
  };

  const handleAddPayment = async (id: string, type: string, data: any) => {
    setActive(true);
    return await addPaymentApi(id, type, data)
      .then((res) => {
        if (res?.data) {
          // Re-fetch member to get updated PaymentType with id
          getMemberApi(String(memberId))
            .then((fullRes) => {
              applyMemberPayments(fullRes.data);
            })
            .catch((err) => console.log(err));
        }
        invalidateCompliance();
        setActive(false);
        return res.data;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleEditPayment = async (id: string, data: any) => {
    setActive(true);
    return await editPaymentApi(id, data)
      .then((res) => {
        getMemberApi(String(memberId))
          .then((fullRes) => applyMemberPayments(fullRes.data))
          .catch((err) => console.log(err));
        invalidateCompliance();
        setActive(false);
        return res.data;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleGenerateDocument = async (id: string, type: string) => {
    if (!id) {
      return { success: false, message: "Please select a document." };
    }
    setActive(true);
    return await generateDocumentApi(id, type, Number(memberId))
      .then(async (res) => {
        try {
          await refreshMember();
        } catch (err) {
          console.log(err);
        }
        invalidateCompliance();
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
        return {
          success: false,
          message: err?.message ?? "Failed to generate document.",
        };
      });
  };

  const handleSendDocuments = async (id: string, data: any) => {
    const validation = validateSelectedDocumentsForSend(
      data?.Documents,
      documents
    );
    if (!validation.isValid) {
      toast.error({
        title: "Cannot Send Documents",
        description:
          validation.message ??
          "One or more selected documents are missing a download link.",
        duration: 7,
      });
      return { success: false, message: validation.message };
    }
    setActive(true);
    return await sendDocumentApi(id, data, currentUser.id)
      .then(async (res) => {
        try {
          await refreshMember();
        } catch {
          /* ignore */
        }
        invalidateCompliance();
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const refreshMember = async () => {
    const fullRes = await getMemberApi(String(memberId));
    setMember(fullRes.data);
    setCoveredLive(fullRes.data.CoveredLive);
    setPayments(fullRes.data.Policy?.Transaction);
    setDocuments(fullRes.data.PersonalDocument);
    setPolicyId(fullRes.data.Policy?.id);
    applyMemberPayments(fullRes.data);
    setAudits(fullRes.data.AuditTrail);
    // Refresh ledger so System / Policy Reinstatement clearance txns appear after activate.
    loadLedger();
    return fullRes;
  };

  const handleLapsePolicy = async (id: string, data: any) => {
    setActive(true);
    try {
      await lapseMemberPolicyApi(id, data, currentUser.id);
      await refreshMember();
      message.success("Policy lapsed.");
    } catch (err) {
      console.log(err);
      message.error("Failed to lapse policy.");
    } finally {
      setActive(false);
    }
  };

  const handleLoadPremium = async (id: string) => {
    setActive(true);
    try {
      await loadMemberPremiumApi(id, currentUser.id);
      await refreshMember();
      message.success("Premium loaded.");
    } catch (err) {
      console.log(err);
      message.error("Failed to load premium.");
    } finally {
      setActive(false);
    }
  };

  const handleActivatePolicy = async (_id: string, data: any = {}) => {
    const validation = validateSACellphone(member?.Cellphone);
    if (!validation.isValid) {
      message.error(validation.message || ACTIVATE_CELLPHONE_TOOLTIP);
      return { success: false, message: validation.message };
    }
    setActive(true);
    try {
      const res = await activateMemberPolicyApi(
        String(memberId),
        data,
        currentUser.id
      );
      if (res?.success === false) {
        message.error(res?.message || "Failed to activate policy.");
        return res;
      }
      await refreshMember();
      message.success("Policy activated.");
      return res ?? { success: true };
    } catch (err: any) {
      console.log(err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to activate policy.";
      message.error(msg);
      return { success: false, message: msg };
    } finally {
      setActive(false);
    }
  };

  const isMemberLapsed =
    member?.PolicyLapsed === true ||
    member?.PolicyLapsed === 1 ||
    String(member?.PolicyLapsed).toLowerCase() === "true";
  const statusId = Number(
    member?.StatusID ?? member?.StatusId ?? member?.Status?.id ?? NaN
  );
  const isMemberInactive = statusId === 2 && !isMemberLapsed;
  const isMemberActive = !isMemberLapsed && !isMemberInactive;

  const openPaymentMethodAction = () => {
    if (payment?.id) {
      setShowEditPaymentModal(true);
    } else {
      setShowAddPaymentModal(true);
    }
  };

  const memberActionsMenu = (
    <Menu>
      {isMemberActive && (
        <>
          <Menu.Item
            key="edit"
            icon={<EditOutlined ref={refEditMember} style={{ color: "blue" }} />}
            onClick={() => handleOpenEditModal()}
          >
            Edit Member
          </Menu.Item>
          <Menu.Item
            key="payment"
            icon={
              <CreditCardOutlined
                ref={refPaymentMethod}
                style={{ color: "orange" }}
              />
            }
            onClick={openPaymentMethodAction}
          >
            Payment Method
          </Menu.Item>
          <Menu.Item
            key="generate"
            icon={
              <FilePdfOutlined
                ref={refGenerateDocument}
                style={{ color: "purple" }}
              />
            }
            onClick={() => setShowGenerateModal(true)}
          >
            Generate Documents
          </Menu.Item>
          <Menu.Item
            key="send"
            icon={<SendOutlined ref={refSendDocument} />}
            onClick={() => setShowSendDocsModal(true)}
          >
            Send Documents
          </Menu.Item>
          <Menu.Item
            key="lapse"
            icon={
              <ExceptionOutlined ref={refLapsePolicy} style={{ color: "red" }} />
            }
            onClick={() => setShowLapseModal(true)}
          >
            Lapse Policy
          </Menu.Item>
          <Menu.Item
            key="cancel"
            icon={<StopOutlined style={{ color: "red" }} />}
            onClick={() => setShowCancelPolicyModal(true)}
          >
            Cancel Policy
          </Menu.Item>
          <Menu.Item
            key="premium"
            icon={
              <DollarOutlined ref={refLoadPremium} style={{ color: "grey" }} />
            }
            onClick={() => setShowPremiumModal(true)}
          >
            Load Premium
          </Menu.Item>
        </>
      )}
      {(isMemberInactive || isMemberLapsed) && (
        <Menu.Item
          key="activate"
          disabled={!isValidSACellphone(member?.Cellphone)}
          icon={
            <FileDoneOutlined ref={refActivatePolicy} style={{ color: "blue" }} />
          }
          onClick={() => setShowActivateModal(true)}
        >
          {!isValidSACellphone(member?.Cellphone) ? (
            <Tooltip title={ACTIVATE_CELLPHONE_TOOLTIP}>
              <span style={{ display: "inline-block", width: "100%" }}>
                Activate Policy
              </span>
            </Tooltip>
          ) : (
            "Activate Policy"
          )}
        </Menu.Item>
      )}
    </Menu>
  );

  const handleRemovePaymentMethod = () => {
    Modal.confirm({
      title: "Remove Payment Method",
      content: "Are you sure you want to remove this payment method? This action cannot be undone.",
      okText: "Remove",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        if (!payment.id) {
          message.error("No payment method ID found.");
          return;
        }
        setActive(true);
        try {
          const res = await removePaymentApi(payment.id);
          if (res?.success || res?.data?.success) {
            const fullRes = await getMemberApi(String(memberId));
            applyMemberPayments(fullRes.data);
            message.success("Payment method removed successfully.");
          } else {
            message.error("Failed to remove payment method.");
          }
        } catch (err) {
          message.error("An error occurred while removing the payment method.");
          console.log(err);
        } finally {
          setActive(false);
        }
      },
    });
  };

  const handleRemoveSecondaryPaymentMethod = () => {
    Modal.confirm({
      title: "Remove 2nd Payment Method",
      content:
        "Are you sure you want to remove the second payment method? This action cannot be undone.",
      okText: "Remove",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        if (!secondaryPayment?.id) {
          message.error("No second payment method ID found.");
          return;
        }
        setActive(true);
        try {
          const res = await removePaymentApi(secondaryPayment.id);
          if (res?.success || res?.data?.success) {
            const fullRes = await getMemberApi(String(memberId));
            applyMemberPayments(fullRes.data);
            message.success("Second payment method removed successfully.");
          } else {
            message.error("Failed to remove second payment method.");
          }
        } catch (err) {
          message.error(
            "An error occurred while removing the second payment method."
          );
          console.log(err);
        } finally {
          setActive(false);
        }
      },
    });
  };

  const handleApplication = () => {
    const blockReason = getApplicationSendBlockReason(documents, member?.Group);
    if (blockReason) {
      message.error(blockReason);
      return;
    }

    setActive(true);
    sendMemberApplicationApi(memberId)
      .then((res) => {
        setActive(false);
        message.success("Application form sent successfully.");
        return res;
      })
      .catch((err) => {
        setActive(false);
        message.error(err);
        console.log(err);
      });
  };

  const handleBack = () => {
    navigate("/members");
  };

  const handleOpenEditModal = () => {
    const res = member;

    if (res.Policy?.Product) {
      setProductInfo(res.Policy.Product);
    }

    const baseData = {
      ...res,
      id: res.id,
      DateOfBirth: dayjs(res.DateOfBirth),
      Identity: {
        value: res.IdType,
        label: res.IdType === 1 ? "South African ID" : "South African Passport",
      },
      InceptionDate: dayjs(res.InceptionDate),
      PolicyNumber: res.Policy?.PolicyNumber,
      PolicyUserID: res.Policy?.User?.id || res.Policy?.UserID,
      ProductType: {
        value: res.Policy?.Product?.PlanCategory?.Name,
        label: res.Policy?.Product?.PlanCategory?.Name,
      },
      Product: {
        value: res.Policy?.Product?.id,
        label: res.Policy?.Product?.ProductName,
      },
      Premium: res.TotalPremium,
      SourceOfFunds: {
        value: res.SourceOfFunds,
        label: res.SourceOfFunds,
      },
      Addons: normalizePolicyAddonsForForm(res.Policy?.Addon || []),
      CoveredLive: (res.CoveredLive || []).map((cl: any) => ({
        id: cl.id,
        CoveredType: cl.CoveredType,
        CoveredName: cl.CoveredName,
        CoveredSurname: cl.CoveredSurname,
        CoveredAmount: cl.CoveredAmount,
        CoveredDateOfBirth: dayjs(cl.CoveredDateOfBirth).startOf("day").format("YYYY-MM-DD"),
        CoveredIdNumber: cl.CoveredIdNumber,
        ExtendedProduct: cl.ExtendedProduct
          ? { value: cl.ExtendedProduct.id, label: cl.ExtendedProduct.ProductName }
          : null,
      })),
    };

    if (res.Beneficiary) {
      setFormattedMember({
        ...baseData,
        ...res.Beneficiary,
        id: res.id,
        BenDateOfBirth: dayjs(res.Beneficiary.BenDateOfBirth),
      });
    } else {
      setFormattedMember(baseData);
    }

    setShowEditModal(true);
  };

  const handleEditMember = async (id: any, data: any, userId: any) => {
    setActive(true);
    return await editMemberApi(String(memberId), data, currentUser.id)
      .then((res) => {
        if (res?.success) {
          // Re-fetch full member data to avoid partial-shape crashes
          return getMemberApi(String(memberId))
            .then((fullRes) => {
              setMember(fullRes.data);
              setCoveredLive(fullRes.data.CoveredLive);
              setPayments(fullRes.data.Policy?.Transaction || []);
              setDocuments(fullRes.data.PersonalDocument);
              applyMemberPayments(fullRes.data);
              setAudits(fullRes.data.AuditTrail);
              invalidateCompliance();
              setActive(false);
              return res;
            })
            .catch(() => {
              setActive(false);
              return res;
            });
        }
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const coveredLiveColumns = [
    {
      title: "Covered Type",
      dataIndex: "CoveredType",
    },
    {
      title: "Name",
      dataIndex: "CoveredName",
    },
    {
      title: "Surname",
      dataIndex: "CoveredSurname",
    },
    {
      title: "ID Number",
      dataIndex: "CoveredIdNumber",
    },
    {
      title: "Date Of Birth",
      dataIndex: "CoveredDateOfBirth",
    },
  ];

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: <span><UserOutlined style={{ marginRight: 4 }} />Member Info</span>,
      children: (
        <div className="content-border">
          <Row style={{ paddingTop: "24px" }} gutter={[24, 24]}>
            <Col span={6}>
              <Card
                title="Personal Info"
                style={{ backgroundColor: "	#DCDCDC", height: "100%" }}
                bordered={true}
                hoverable={true}
              >
                <div className="card-content">
                  <h4>ID Number</h4>
                  <span>{member.IdNumber}</span>
                </div>
                <div className="card-content">
                  <h4>Date Of Birth:</h4>
                  <span>{member.DateOfBirth}</span>
                </div>
                <div className="card-content">
                  <h4>Gender:</h4>
                  <span>{member.Gender}</span>
                </div>
                <div className="card-content">
                  <h4>Title:</h4>
                  <span> {member.Title}</span>
                </div>
                <div className="card-content">
                  <h4>Name:</h4>
                  <span>{member.Name}</span>
                </div>
                <div className="card-content">
                  <h4>Surname:</h4>
                  <span>{member.Surname}</span>
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card
                title="Additional Info"
                style={{ backgroundColor: "	#DCDCDC", height: "100%" }}
                bordered={true}
                hoverable={true}
              >
                <div className="card-content">
                  <h4>Telephone Number:</h4>
                  <span>{member.TelNumber}</span>
                </div>
                <div className="card-content">
                  <h4>Cellphone:</h4>
                  <span>{member.Cellphone}</span>
                </div>
                {member.Cellphone &&
                  !isValidSACellphone(member.Cellphone) && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Invalid cellphone number"
                      description="This number is not in valid SA format. Edit the member to correct it before activating the policy."
                    />
                  )}
                <div className="card-content">
                  <h4>Email:</h4>
                  <span>{member.Email}</span>
                </div>
                <div className="card-content">
                  <h4>Inception Date:</h4>
                  <span>{member.InceptionDate}</span>
                </div>
                <div className="card-content">
                  <h4>Group:</h4>
                  <span> {member.Group?.GroupName}</span>
                </div>
                <div className="card-content">
                  <h4>Product:</h4>
                  <span>{member.Policy?.Product?.ProductName}</span>
                </div>
                <div
                  className="card-content"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div style={{ marginRight: "20px" }}>
                    <h4>Policy Premium:</h4>
                    <span>
                      R
                      {(() => {
                        let totalPolicyPremium = Number(
                          member.Policy?.Product.PolicyPremium || 0
                        );

                        if (
                          member.CoveredLive &&
                          member.CoveredLive.length > 0
                        ) {
                          totalPolicyPremium = member.CoveredLive.reduce(
                            (total: number, cl: any) => {
                              if (
                                cl.ExtendedProduct &&
                                cl.ExtendedProduct.PolicyPremium
                              ) {
                                return (
                                  total +
                                  Number(cl.ExtendedProduct.PolicyPremium)
                                );
                              }
                              return total;
                            },
                            totalPolicyPremium // Start with the initial policy premium
                          );
                        }

                        return totalPolicyPremium;
                      })()}
                    </span>
                  </div>

                  {/* TotalPremium from the API already includes addon premiums */}
                  <div>
                    <h4>Total Deduction:</h4>
                    <span>R{Number(member.TotalPremium || 0)}</span>
                  </div>
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card
                title="Residential Info"
                style={{ backgroundColor: "	#DCDCDC", height: "100%" }}
                bordered={true}
                hoverable={true}
              >
                <div className="card-content">
                  <h4>Address Line 1:</h4>
                  <span> {member.AddressLine1}</span>
                </div>
                <div className="card-content">
                  <h4>Address Line 1:</h4>
                  <span>{member.AddressLine2}</span>
                </div>
                <div className="card-content">
                  <h4>Suburb:</h4>
                  <span>{member.Suburb}</span>
                </div>
                <div className="card-content">
                  <h4>City/Town:</h4>
                  <span>{member.CityTown}</span>
                </div>
                <div className="card-content">
                  <h4>Province:</h4>
                  <span> {member.Province}</span>
                </div>
                <div className="card-content">
                  <h4>Postal Code:</h4>
                  <span>{member.PostalCode}</span>
                </div>
              </Card>
            </Col>
            <Col span={6}>
              <Card
                title="Benficiary Info"
                style={{ backgroundColor: "	#DCDCDC", height: "100%" }}
                bordered={true}
                hoverable={true}
              >
                <div className="card-content">
                  <h4>ID Number</h4>
                  <span>{member.Beneficiary?.BenIdNumber}</span>
                </div>
                <div className="card-content">
                  <h4>Date Of Birth:</h4>
                  <span>{member.Beneficiary?.BenDateOfBirth}</span>
                </div>
                <div className="card-content">
                  <h4>Gender:</h4>
                  <span>{member.Beneficiary?.BenGender}</span>
                </div>
                <div className="card-content">
                  <h4>Title:</h4>
                  <span> {member.Beneficiary?.BenTitle}</span>
                </div>
                <div className="card-content">
                  <h4>Name:</h4>
                  <span>{member.Beneficiary?.BenName}</span>
                </div>
                <div className="card-content">
                  <h4>Surname:</h4>
                  <span>{member.Beneficiary?.BenSurname}</span>
                </div>
              </Card>
            </Col>
          </Row>

          <h3 style={{ margin: "16px 0" }}>
            <TeamOutlined
              style={{
                paddingRight: "5px",
                color: "#f04f05",
              }}
            />
            Covered Lives
          </h3>
          <Table
            dataSource={coveredLive}
            columns={coveredLiveColumns}
            pagination={false}
          />
        </div>
      ),
    },
    {
      key: "2",
      label: <span><CreditCardOutlined style={{ marginRight: 4 }} />Payment Info</span>,
      children: (
        <div className="content-border">
          <Row style={{ paddingTop: "24px" }} gutter={[24, 24]}>
            <Col span={12}>
              <Card
                title="Payment Info"
                style={{ backgroundColor: "	#DCDCDC", height: "100%" }}
                bordered={true}
                hoverable={true}
                extra={
                  (currentUser?.Role?.Name === "Admin" || currentUser?.Role?.Name === "Super Admin") ? (
                    payment.id ? (
                      <Button danger onClick={handleRemovePaymentMethod}>
                        Remove Payment Method
                      </Button>
                    ) : (
                      <Button type="primary" onClick={() => setShowAddPaymentModal(true)}>
                        Add Payment Method
                      </Button>
                    )
                  ) : null
                }
              >
                {renderPaymentDetails(payment)}
              </Card>
            </Col>
            <Col span={12}>
              <Card
                title="2nd Payment Method"
                style={{ backgroundColor: "	#DCDCDC", height: "100%" }}
                bordered={true}
                hoverable={true}
                extra={
                  currentUser?.Role?.Name === "Admin" ||
                  currentUser?.Role?.Name === "Super Admin" ? (
                    secondaryPayment?.id ? (
                      <Button danger onClick={handleRemoveSecondaryPaymentMethod}>
                        Remove
                      </Button>
                    ) : (
                      <Button
                        type="primary"
                        disabled={!payment?.id}
                        onClick={() => setShowAddSecondaryPaymentModal(true)}
                      >
                        Add
                      </Button>
                    )
                  ) : null
                }
              >
                {secondaryPayment?.id ? (
                  renderPaymentDetails(secondaryPayment)
                ) : (
                  <span style={{ color: "#8c8c8c" }}>
                    {payment?.id
                      ? "No second payment method. Choose a payment type to add one."
                      : "Add a primary payment method first."}
                  </span>
                )}
              </Card>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: "3",
      label: <span><DollarOutlined style={{ marginRight: 4 }} />Payments</span>,
      children: (
        <div className="content-border">
          <PaymentTable
            payments={payments}
            member={member}
            memberId={memberId}
            handleAdd={handleAddTransaction}
            handleDelete={handleDeleteTransaction}
            policyId={policyId}
          />
        </div>
      ),
    },
    {
      key: "4",
      label: <span><FileTextOutlined style={{ marginRight: 4 }} />Documents</span>,
      children: (
        <div className="content-border">
          {documentsMissingLink.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Documents missing download links"
              description={`These generated documents cannot be sent until regenerated: ${documentsMissingLink
                .map((doc: any) => doc?.Name ?? "Unnamed document")
                .join(", ")}.`}
            />
          )}
          <Row gutter={[12, 12]} className="document-container">
            <Documentlist
              memberId={memberId}
              documents={documents}
              handleAddDocument={handleAddDocument}
              handleDownloadDocument={handleDownloadDocument}
              handleDeleteDocument={handleDeleteDocument}
            />
          </Row>
        </div>
      ),
    },
    {
      key: "7",
      label: (
        <span>
          <FileTextOutlined style={{ marginRight: 4 }} />Claims
        </span>
      ),
      children: (
        <div>
          <Table
            loading={claimsLoading}
            dataSource={claimsData}
            rowKey="id"
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            locale={{ emptyText: "No claims found for this member's policy" }}
            columns={[
              {
                title: "Claim No",
                dataIndex: "ClaimNumber",
              },
              {
                title: "Policy Number",
                dataIndex: ["Policy", "PolicyNumber"],
              },
              {
                title: "Deceased Name",
                render: (_: any, r: any) =>
                  `${r.DeceasedName ?? ""} ${r.DeceasedSurname ?? ""}`.trim() || "-",
              },
              {
                title: "Cause Of Death",
                dataIndex: "CauseOfDeath",
                render: (v: any) => v || "-",
              },
              {
                title: "Date Of Death",
                dataIndex: "DateOfDeath",
                render: (v: any) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
              },
              {
                title: "Claim Amount",
                dataIndex: "ClaimAmount",
                render: (v: any) =>
                  v != null ? `R${Number(v).toFixed(2)}` : "-",
              },
              {
                title: "Status",
                dataIndex: "Status",
              },
              {
                title: "Claim Date",
                dataIndex: "Date",
                render: (v: any) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
              },
              {
                title: "",
                dataIndex: "id",
                render: (id: string) => (
                  <Button size="small" type="link" onClick={() => navigate(`/claims/${id}`)}>
                    Manage
                  </Button>
                ),
              },
            ]}
          />
        </div>
      ),
    },
    {
      key: "5",
      label: (
        <span>
          <WalletOutlined style={{ marginRight: 4 }} />
          Account Statement
        </span>
      ),
      children: (
        <div className="content-border">
          <Table
            dataSource={ledgerData}
            loading={ledgerLoading}
            rowKey="id"
            pagination={{ pageSize: 15, hideOnSinglePage: true }}
            columns={[
              {
                title: "Date",
                dataIndex: "Date",
                render: (val: string) => formatTransactionDate(val, "YYYY-MM-DD"),
              },
              {
                title: "Type",
                dataIndex: "Type",
              },
              {
                title: "Reference",
                dataIndex: "Reference",
              },
              {
                title: "Note",
                dataIndex: "Note",
                render: (val: string) => val || "-",
              },
              {
                title: "Provider",
                dataIndex: "PaymentProvider",
              },
              {
                title: "Processed",
                dataIndex: "DateProcessed",
                render: (val: string) =>
                  formatTransactionDate(val, "YYYY-MM-DD"),
              },
              {
                title: "Debit (R)",
                dataIndex: "Amount",
                render: (val: string, record: any) => {
                  const num = Number(val);
                  const isDebit = num < 0 || record.Type === "Refund";
                  return isDebit ? (
                    <span style={{ color: "#cf1322", fontWeight: 600 }}>
                      R{Math.abs(num).toFixed(2)}
                    </span>
                  ) : "-";
                },
              },
              {
                title: "Credit (R)",
                dataIndex: "Amount",
                render: (val: string, record: any) => {
                  const num = Number(val);
                  const isCredit = num > 0 && record.Type !== "Refund";
                  return isCredit ? (
                    <span style={{ color: "#389e0d", fontWeight: 600 }}>
                      R{num.toFixed(2)}
                    </span>
                  ) : "-";
                },
              },
            ]}
            summary={() => {
              return (
                <Table.Summary.Row style={{ fontWeight: 700, background: "#fafafa" }}>
                  <Table.Summary.Cell index={0} colSpan={6}>Total</Table.Summary.Cell>
                  <Table.Summary.Cell index={6}>
                    <span style={{ color: "#cf1322" }}>R{totalLedgerDebit.toFixed(2)}</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7}>
                    <span style={{ color: "#389e0d" }}>R{totalLedgerCredit.toFixed(2)}</span>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        </div>
      ),
    },
    {
      key: "6",
      label: <span><HistoryOutlined style={{ marginRight: 4 }} />Audit Trail</span>,
      children: (
        <div className="content-border">
          <AuditTable audits={audits} />
        </div>
      ),
    },
  ];

  useEffect(() => {
    getMemberApi(String(memberId))
      .then((res) => {
        setMember(res.data);
        setCoveredLive(res.data.CoveredLive);
        setPayments(res.data.Policy.Transaction);
        setDocuments(res.data.PersonalDocument);
        setPolicyId(res.data.Policy.id);
        applyMemberPayments(res.data);
        setAudits(res.data.AuditTrail);
      })
      .catch((err) => {
        console.log(err);
      });

    setActive(false);
  }, [memberId, isActive]);

  return (
    <LoadingOverlay active={isActive} spinner>
      <Layout style={{ minHeight: "100vh" }}>
        <SideBar />
        <Layout className="site-layout">
          <TopBar />
          <Content>
            <div
              style={{
                padding: 24,
                minHeight: "100%",
                background: colorBgContainer,
              }}
            >
              <div style={{ marginBottom: "24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <h2 style={{ marginBottom: 0, fontSize: "24px", fontWeight: "600", display: "flex", alignItems: "center", gap: "12px" }}>
                  <span>
                    <UserOutlined
                      style={{
                        paddingRight: "8px",
                        color: "#f04f05",
                      }}
                    />
                    {`${member.Name} ${member.Surname}`}
                  </span>
                  {member.PolicyLapsed && member.LapsedReason && (
                    <Tag color="red" style={{ fontSize: "14px", fontWeight: 500, marginInlineEnd: 0 }}>
                      Policy Status: {member.LapsedReason}
                    </Tag>
                  )}
                </h2>
                <h2 style={{ marginBottom: 0, fontSize: "24px", fontWeight: "600" }}>
                  <SolutionOutlined style={{ paddingRight: "8px", color: "#f04f05" }} />
                  {member.Policy?.PolicyNumber}
                </h2>
              </div>
              <div className="interactions">
                <Button onClick={handleBack} size="large" style={{ flex: "0 0 auto" }}>
                  <ArrowLeftOutlined style={{ paddingRight: "5px" }} />
                  Back
                </Button>

                <div style={{ flex: "1", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "10px" }}>
                  <Tooltip
                    title={
                      applicationSendBlockReason ??
                      "Send the digital application form link via SMS"
                    }
                  >
                    <span>
                      <Button
                        onClick={handleApplication}
                        size="large"
                        disabled={!!applicationSendBlockReason}
                      >
                        <SolutionOutlined style={{ paddingRight: "5px" }} />
                        Send Application Form
                      </Button>
                    </span>
                  </Tooltip>
                  <Button onClick={handleOpen} size="large">
                    <MailOutlined style={{ paddingRight: "5px" }} />
                    Send SMS
                  </Button>
                  <Dropdown overlay={memberActionsMenu} trigger={["click"]}>
                    <Button size="large">
                      Actions <DownOutlined />
                    </Button>
                  </Dropdown>
                  <EditMemberModal
                    show={showEditModal}
                    member={formattedMember}
                    showModal={() => setShowEditModal(!showEditModal)}
                    handleUpdate={handleEditMember}
                  />
                  <AddPaymentMethodModal
                    show={showAddPaymentModal}
                    showModal={() => {
                      if (showAddPaymentModal) {
                        getMemberApi(String(memberId))
                          .then((res) => {
                            applyMemberPayments(res.data);
                          })
                          .catch((err) => console.log(err));
                      }
                      setShowAddPaymentModal(!showAddPaymentModal);
                    }}
                    handleAdd={handleAddPayment}
                    handleEdit={() => {}}
                    memberId={String(memberId)}
                  />
                  <AddPaymentMethodModal
                    show={showAddSecondaryPaymentModal}
                    isSecondary
                    primaryPaymentType={payment?.Type}
                    showModal={() => {
                      if (showAddSecondaryPaymentModal) {
                        getMemberApi(String(memberId))
                          .then((res) => {
                            applyMemberPayments(res.data);
                          })
                          .catch((err) => console.log(err));
                      }
                      setShowAddSecondaryPaymentModal(
                        !showAddSecondaryPaymentModal
                      );
                    }}
                    handleAdd={handleAddPayment}
                    handleEdit={() => {}}
                    memberId={String(memberId)}
                  />
                  {payment?.id && (
                    <EditPaymentMethodModal
                      show={showEditPaymentModal}
                      payment={payment}
                      showModal={() =>
                        setShowEditPaymentModal(!showEditPaymentModal)
                      }
                      handleEdit={handleEditPayment}
                      memberId={String(memberId)}
                    />
                  )}
                  <GenerateDocumentModal
                    show={showGenerateModal}
                    showModal={() => setShowGenerateModal(!showGenerateModal)}
                    handleGenerate={handleGenerateDocument}
                    memberId={String(memberId)}
                  />
                  <SendDocumentModal
                    show={showSendDocsModal}
                    showModal={() => setShowSendDocsModal(!showSendDocsModal)}
                    handleSend={handleSendDocuments}
                    memberId={String(memberId)}
                  />
                  <LapsePolicyModal
                    show={showLapseModal}
                    showModal={() => setShowLapseModal(!showLapseModal)}
                    handleLapse={handleLapsePolicy}
                    memberId={String(memberId)}
                  />
                  <ActivatePolicyModal
                    show={showActivateModal}
                    showModal={() => setShowActivateModal(!showActivateModal)}
                    handleActivate={handleActivatePolicy}
                    memberId={String(memberId)}
                    currentInceptionDate={member?.InceptionDate}
                  />
                  <CancelPolicyModal
                    show={showCancelPolicyModal}
                    showModal={() =>
                      setShowCancelPolicyModal(!showCancelPolicyModal)
                    }
                    handleCancel={handleLapsePolicy}
                    memberId={String(memberId)}
                  />
                  <LoadPremiumModal
                    show={showPremiumModal}
                    showModal={() => setShowPremiumModal(!showPremiumModal)}
                    handlePremium={handleLoadPremium}
                    memberId={String(memberId)}
                  />

                  <Modal
                    title="Send SMS"
                    open={isModalOpen}
                    onOk={handleSendSMS}
                    onCancel={handleCancel}
                    okText="Send"
                    cancelText="Cancel"
                  >
                    <Input.TextArea
                      rows={4}
                      value={smsContent}
                      onChange={(e) => setSmsContent(e.target.value)}
                      placeholder="Write your SMS content here..."
                    />
                  </Modal>
                </div>
              </div>
              <div className="member-tabs-split">
                <style>{`
                  .member-tabs-split .ant-tabs-nav-list {
                    min-width: 100%;
                  }
                  .member-tabs-split .ant-tabs-tab:nth-child(6) {
                    margin-left: auto !important;
                  }
                `}</style>
                <Tabs
                  type="card"
                  activeKey={activeTab}
                  items={items}
                  onChange={onChange}
                  style={{ marginTop: "24px" }}
                />
              </div>
            </div>
          </Content>
        </Layout>
      </Layout>
    </LoadingOverlay>
  );
};

export default MemberManagementPage;
